import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { CalendarIcon, X } from "lucide-react";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type ActivityFilters = {
  creator: string;
  activityType: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
};

interface ActivityFiltersProps {
  filters: ActivityFilters;
  onFiltersChange: (filters: ActivityFilters) => void;
}

export function ActivityFilters({ filters, onFiltersChange }: ActivityFiltersProps) {
  const { data: creators } = useQuery({
    queryKey: ["creators-list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("creators")
        .select("creator_id, name, surname, nick_name")
        .order("surname");

      if (error) {
        console.error("Error fetching creators:", error);
        throw error;
      }

      return data;
    },
  });

  const handleFilterChange = (key: keyof ActivityFilters, value: any) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const handleClearDate = (
    e: React.MouseEvent,
    dateType: "startDate" | "endDate"
  ) => {
    e.preventDefault();
    e.stopPropagation();
    handleFilterChange(dateType, undefined);
  };

  return (
    <div className="grid grid-cols-4 gap-4 mb-4">
      <Select
        value={filters.creator}
        onValueChange={(value) => handleFilterChange("creator", value)}
      >
        <SelectTrigger>
          <SelectValue placeholder="Filter by creator" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Creators</SelectItem>
          {creators?.map((creator) => (
            <SelectItem key={creator.creator_id} value={creator.creator_id.toString()}>
              {creator.nick_name 
                ? `${creator.nick_name} / ${creator.name} ${creator.surname}`
                : `${creator.name} ${creator.surname}`}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.activityType}
        onValueChange={(value) => handleFilterChange("activityType", value)}
      >
        <SelectTrigger>
          <SelectValue placeholder="Filter by type" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Types</SelectItem>
          <SelectItem value="sign_in">Sign In</SelectItem>
          <SelectItem value="sign_out">Sign Out</SelectItem>
          <SelectItem value="artwork_created">Artwork Created</SelectItem>
          <SelectItem value="edition_created">Edition Created</SelectItem>
          <SelectItem value="print_ordered">Print Ordered</SelectItem>
          <SelectItem value="profile_updated">Profile Updated</SelectItem>
        </SelectContent>
      </Select>

      <div className="relative">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant={"outline"}
              className={cn(
                "w-full justify-start text-left font-normal",
                !filters.startDate && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {filters.startDate ? format(filters.startDate, "PPP") : "Start date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={filters.startDate}
              onSelect={(date) => handleFilterChange("startDate", date)}
              initialFocus
            />
          </PopoverContent>
        </Popover>
        {filters.startDate && (
          <div 
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10"
            onClick={(e) => handleClearDate(e, "startDate")}
          >
            <X className="h-4 w-4 hover:text-destructive cursor-pointer" />
          </div>
        )}
      </div>

      <div className="relative">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant={"outline"}
              className={cn(
                "w-full justify-start text-left font-normal",
                !filters.endDate && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {filters.endDate ? format(filters.endDate, "PPP") : "End date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={filters.endDate}
              onSelect={(date) => handleFilterChange("endDate", date)}
              initialFocus
            />
          </PopoverContent>
        </Popover>
        {filters.endDate && (
          <div 
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10"
            onClick={(e) => handleClearDate(e, "endDate")}
          >
            <X className="h-4 w-4 hover:text-destructive cursor-pointer" />
          </div>
        )}
      </div>
    </div>
  );
}